import { useCallback, useState } from "react";

const memoryStorage = new Map<string, unknown>();

export function useLocalStorage<T>(key: string, defaultValue?: T) {
  return useStorage(key, defaultValue, window.localStorage);
}

export function useSessionStorage<T>(key: string, defaultValue?: T) {
  return useStorage(key, defaultValue, window.sessionStorage);
}

export function useSessionMemory<T>(key: string, defaultValue?: T) {
  return useStorage(key, defaultValue, memoryStorage);
}

function useStorage<T>(
  key: string,
  defaultValue: T | undefined,
  storageObject: Storage | Map<string, unknown>
) {
  const [value, setValue] = useState<T | undefined>(() => {
    if (storageObject instanceof Map) {
      return storageObject.get(key) ?? defaultValue;
    }
    // Reading is what throws, not only writing: Safari in private browsing and
    // a profile with cookies blocked both raise on `getItem`, and a throw in
    // the initializer takes the whole component down with it — the composer,
    // typically, which is the app.
    let storedValue: string | null = null;
    try {
      storedValue = storageObject.getItem(key);
    } catch {
      storedValue = null;
    }
    if (storedValue != null) {
      try {
        return JSON.parse(storedValue);
      } catch {
        return storedValue as unknown as T;
      }
    }
    return typeof defaultValue === "function" ? defaultValue() : defaultValue;
  });

  const setNewValue = (newValue: T | undefined) => {
    setValue(newValue);
    if (newValue === undefined) {
      if (storageObject instanceof Map) {
        storageObject.delete(key);
      } else {
        try {
          storageObject.removeItem(key);
        } catch {
          // Same as above: unreadable storage is also unwritable.
        }
      }
    } else if (newValue instanceof File) {
      if (storageObject instanceof Map) {
        storageObject.set(key, newValue);
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          storageObject.setItem(
            key,
            JSON.stringify({
              base64: base64String,
              name: newValue.name,
              type: newValue.type,
            })
          );
        };
        reader.readAsDataURL(newValue);
      }
    } else {
      try {
        if (storageObject instanceof Map) {
          storageObject.set(key, newValue);
        } else {
          const val =
            typeof newValue === "string" ? newValue : JSON.stringify(newValue);
          storageObject.setItem(key, val);
        }
      } catch {
        if (!(storageObject instanceof Map)) {
          // The retry can throw for the very reason the first attempt did —
          // storage unavailable rather than the value unserializable. The
          // value stays in state either way, which is what the caller reads.
          try {
            storageObject.setItem(key, String(newValue));
          } catch {
            // Nothing left to do: this session keeps it in memory.
          }
        }
      }
    }
  };

  const remove = useCallback(() => {
    setNewValue(undefined);
  }, []);

  return [value, setNewValue, remove] as const;
}
